// Global Instructions Rule Applied!
//
// Late-joiner Fee Reallocation section (fund groups; Origin SBIC LPA
// § 5.04(e), owner rulings 2026-09-28). Rendered on the group's Capital
// Calls tab, below Equalization, for every satellite on the late-closer
// policy 'Reallocate fees to date'.
//
// The satellite charges no catch-up fee: its fees to date are re-split over
// every partner admitted, and the primary's § 11.9(b) credit follows. The
// cash is not reallocated - the late joiner's same-% transfer is shown as
// made on the Transfers grid. This section books the ACCOUNTING as at the
// admission date, in order:
//   1. the satellite fee true-up (self-truing Management Fees transaction),
//   2. the primary fee true-up once (1) is Booked,
//   3. the contribution-bucket reclass journal entries (satellite + primary
//      mirror; each pocket nets to 0).
// Draft Reallocation drafts whatever step is due; each Draft opens in its
// own form for review and Post. The tables are the live position, so after
// every step is booked they read 0.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Modal, Select, Space, Spin, Table, Tag, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { FormSection } from '../../../core/components/FormSection.tsx'
import NumberText from '../../../core/components/NumberText.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { TABLE_FUND_TRANSACTIONS, TABLE_JOURNAL_ENTRIES } from '../../../state_manager/globals.ts'
import {
  FundGroupFeeReallocation,
  FundGroupFeeReallocationEvent,
  FundGroupFeeReallocationSatellite,
  FundGroupFeeReclassRow
} from '../../../state_manager/data_models/fund_group.ts'
import {
  advanceFundGroupFeeReallocation,
  deleteFundGroupFeeReallocation,
  fetchFundGroupFeeReallocation
} from '../services/fundGroupsService.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'

interface Props {
  fundGroupId: string
}

interface FeeRow {
  key: string
  investorName: string
  satelliteDelta: number | null
  primaryDelta: number | null
}

interface StepRow {
  key: string
  step: string
  amount: number | null
  status: string
  statusColor: string
  hint?: string
  open?: () => void
}

const fmtDate = (d: string | null | undefined): string => (d ? dayjs(d).format(DATE_FORMAT) : '—')

// the Tag vocabulary: Draft = blue, Booked = green, needs attention = gold,
// nothing to do = default
const statusTag = (status: string | null | undefined): { label: string; color: string } => {
  if (status === 'Booked') return { label: 'Booked', color: 'green' }
  if (status === 'Draft') return { label: 'Draft', color: 'blue' }
  return { label: status ?? '—', color: 'default' }
}

export function FundGroupFeeReallocationSection({ fundGroupId }: Props): React.ReactElement | null {
  const { openFormInTab } = useTabManagerContext()
  const [data, setData] = useState<FundGroupFeeReallocation | null>(null)
  const [fundId, setFundId] = useState<string | null>(null)
  const [asOf, setAsOf] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [acting, setActing] = useState(false)
  // static Modal.confirm does not render under React 19 - hook form required
  const [modalApi, modalContextHolder] = Modal.useModal()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchFundGroupFeeReallocation(fundGroupId, asOf, getAccessToken(store.getState().auth))
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to load the fee reallocation', { key: 'fee-reallocation-load' })
        return
      }
      setData(res.data)
    } finally {
      setLoading(false)
    }
  }, [fundGroupId, asOf])

  useEffect(() => {
    void load()
  }, [load])

  // the Drafts are posted in their own form tabs: refresh when attention
  // returns here
  const activateRef = useRefreshOnActivate(load)

  const satellites = useMemo(() => data?.satellites ?? [], [data])
  const sat: FundGroupFeeReallocationSatellite | null = useMemo(
    () => satellites.find((s) => s.funds__id === fundId) ?? satellites[0] ?? null,
    [satellites, fundId]
  )
  const event: FundGroupFeeReallocationEvent | null = useMemo(
    () => sat?.prog__events.find((e) => e.fund_group_fee_reallocations__as_of === sat.prog__as_of) ?? null,
    [sat]
  )

  const openTransaction = useCallback(
    (id: string | null | undefined, title: string) => {
      if (id) openFormInTab(TABLE_FUND_TRANSACTIONS, id, title)
    },
    [openFormInTab]
  )
  const openEntry = useCallback(
    (id: string | null | undefined, title: string) => {
      if (id) openFormInTab(TABLE_JOURNAL_ENTRIES, id, title)
    },
    [openFormInTab]
  )

  const handleDraft = useCallback(() => {
    if (!sat) return
    modalApi.confirm({
      title: 'Draft the fee reallocation?',
      content: `Drafts the reallocation steps that are due for ${sat.funds__name ?? 'the satellite'} as at ${fmtDate(
        sat.prog__as_of
      )}: the ${sat.funds__name ?? 'satellite'} fee true-up, the ${
        sat.prog__primary_fund_name ?? 'primary'
      } fee true-up once the first is posted, and the contribution reclass entries. Review and post each Draft in its own form.`,
      okText: 'Draft',
      onOk: async () => {
        setActing(true)
        try {
          const res = await advanceFundGroupFeeReallocation(
            { fund_group_id: fundGroupId, fund_id: sat.funds__id, as_of: sat.prog__as_of },
            getAccessToken(store.getState().auth)
          )
          if (!res.success || !res.data) {
            showErrorToast(res.error || 'Failed to draft the reallocation')
            return
          }
          const created = res.data.created
          if (created.satellite_fee_transaction_id || created.primary_fee_transaction_id
              || created.satellite_journal_entry_id) {
            toast.success(res.data.status_message || 'Draft reallocation created')
          } else {
            toast.info(res.data.status_message || 'Nothing to draft')
          }
          const label = `Fee reallocation ${fmtDate(sat.prog__as_of)}`
          if (created.satellite_fee_transaction_id) {
            openTransaction(created.satellite_fee_transaction_id, `${label} — ${sat.funds__name ?? ''}`)
          } else if (created.primary_fee_transaction_id) {
            openTransaction(created.primary_fee_transaction_id, `${label} — ${sat.prog__primary_fund_name ?? ''}`)
          }
          await load()
        } finally {
          setActing(false)
        }
      }
    })
  }, [fundGroupId, load, modalApi, openTransaction, sat])

  const handleDelete = useCallback(
    (e: FundGroupFeeReallocationEvent) => {
      modalApi.confirm({
        title: 'Delete the draft reallocation?',
        content: 'Its Draft fee transactions and journal entries are moved to trash. Booked records must be unbooked on their own forms first.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          setActing(true)
          try {
            const res = await deleteFundGroupFeeReallocation(
              e.fund_group_fee_reallocations__id,
              getAccessToken(store.getState().auth)
            )
            if (!res.success) {
              showErrorToast(res.error || 'Failed to delete the reallocation')
              return
            }
            toast.success('Draft reallocation deleted')
            await load()
          } finally {
            setActing(false)
          }
        }
      })
    },
    [load, modalApi]
  )

  const figure = (v: number | null | undefined, strong = false) => (
    <NumberText value={v ?? 0} wrap={strong ? (f) => <Text strong>{f}</Text> : undefined} />
  )

  // ----- steps: what is due, drafted or booked for the selected date -----
  const steps = useMemo((): StepRow[] => {
    if (!sat) return []
    const satName = sat.funds__name ?? 'Satellite'
    const priName = sat.prog__primary_fund_name ?? 'Primary'
    const label = `Fee reallocation ${fmtDate(sat.prog__as_of)}`
    const feeStep = (
      key: string,
      step: string,
      open: boolean | null | undefined,
      delta: number | null | undefined,
      txStatus: string | null | undefined,
      txId: string | null | undefined,
      title: string,
      waiting?: string
    ): StepRow => {
      if (txStatus) {
        const t = statusTag(txStatus)
        return {
          key, step, amount: delta ?? 0, status: t.label, statusColor: t.color,
          open: () => openTransaction(txId, title)
        }
      }
      if (open) {
        return waiting
          ? { key, step, amount: delta ?? 0, status: 'Waiting', statusColor: 'gold', hint: waiting }
          : { key, step, amount: delta ?? 0, status: 'Due', statusColor: 'gold' }
      }
      return { key, step, amount: 0, status: 'Nothing to book', statusColor: 'default' }
    }
    // the reclass is two journal entries - the satellite's and the primary's
    // mirror - each posted on its own form, so each gets its own step row
    const reclassNet =
      Math.round((sat.prog__reclass_rows ?? []).reduce((acc, r) => acc + Math.abs(r.prog__delta_fees ?? 0), 0) * 100) / 100
    const reclassStep = (
      key: string,
      step: string,
      jeStatus: string | null | undefined,
      jeId: string | null | undefined,
      title: string
    ): StepRow => {
      if (jeStatus) {
        const t = statusTag(jeStatus)
        return { key, step, amount: reclassNet, status: t.label, statusColor: t.color, open: () => openEntry(jeId, title) }
      }
      if (!sat.prog__reclass_in_sync) {
        return {
          key, step, amount: null, status: 'Held', statusColor: 'gold',
          hint: 'A pocket’s transferred total differs from its pro-rata share — book the late joiner’s same-% transfer first'
        }
      }
      return sat.prog__reclass_needed
        ? { key, step, amount: reclassNet, status: 'Due', statusColor: 'gold' }
        : { key, step, amount: 0, status: 'Nothing to book', statusColor: 'default' }
    }
    return [
      feeStep('sat', `${satName} fee true-up`, sat.prog__satellite_fee_open, sat.prog__satellite_fee_delta,
              event?.prog__satellite_fee_status, event?.prog__satellite_fee_transaction_id, `${label} — ${satName}`),
      feeStep('pri', `${priName} fee true-up`, sat.prog__primary_fee_open, sat.prog__primary_fee_delta,
              event?.prog__primary_fee_status, event?.prog__primary_fee_transaction_id, `${label} — ${priName}`,
              sat.prog__primary_fee_ready ? undefined : `${satName} must book its fees through ${fmtDate(sat.prog__period_end)} first`),
      reclassStep('reclass-sat', `${satName} contribution reclass`, event?.prog__satellite_journal_status,
                  event?.prog__satellite_journal_entry_id, `${label} — ${satName} reclass`),
      reclassStep('reclass-pri', `${priName} contribution reclass (mirror)`, event?.prog__primary_journal_status,
                  event?.prog__primary_journal_entry_id, `${label} — ${priName} reclass`)
    ]
  }, [event, openEntry, openTransaction, sat])

  // ----- per-investor fee true-up: satellite Δ and primary Δ side by side -----
  const feeRows = useMemo((): FeeRow[] => {
    if (!sat) return []
    const satByPocket = new Map(sat.prog__satellite_fee_rows.map((r) => [r.fund_commitments__id, r]))
    const priByPocket = new Map(sat.prog__primary_fee_rows.map((r) => [r.fund_commitments__id, r]))
    return (sat.prog__reclass_rows ?? []).map((r: FundGroupFeeReclassRow) => ({
      key: r.fund_group_commitments__id,
      investorName: r.investors__name ?? '—',
      satelliteDelta: satByPocket.get(r.fund_commitments__id)?.prog__delta_total ?? null,
      primaryDelta: r.prog__primary_commitment_id
        ? priByPocket.get(r.prog__primary_commitment_id)?.prog__delta_total ?? null
        : null
    }))
  }, [sat])

  const stepColumns = [
    { title: 'Step', dataIndex: 'step', key: 'step' },
    {
      // the live position: what is still to book (0 once the step is booked)
      title: 'To book',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      width: 150,
      render: (v: number | null) => (v === null ? <Text type='secondary'>—</Text> : figure(v))
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 280,
      render: (v: string, r: StepRow) => (
        <Space size={4} wrap>
          <Tag color={r.statusColor}>{v}</Tag>
          {r.hint ? <Text type='secondary'>{r.hint}</Text> : null}
        </Space>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, r: StepRow) =>
        r.open ? (
          <Button size='small' onClick={r.open}>
            Open
          </Button>
        ) : null
    }
  ]

  const feeColumns = [
    { title: 'Investor', dataIndex: 'investorName', key: 'investor' },
    {
      title: `${sat?.funds__name ?? 'Satellite'} Δ`,
      dataIndex: 'satelliteDelta',
      key: 'sat',
      align: 'right' as const,
      width: 170,
      render: (v: number | null) => figure(v)
    },
    {
      title: `${sat?.prog__primary_fund_name ?? 'Primary'} Δ`,
      dataIndex: 'primaryDelta',
      key: 'pri',
      align: 'right' as const,
      width: 170,
      render: (v: number | null) => figure(v)
    }
  ]

  const reclassColumns = [
    { title: 'Investor', dataIndex: 'investors__name', key: 'investor', render: (v: string | null) => v ?? '—' },
    {
      title: 'Admitted',
      dataIndex: 'fund_commitments__date_closed',
      key: 'closed',
      width: 110,
      render: (v: string | null) => fmtDate(v)
    },
    {
      title: 'Commitment',
      dataIndex: 'fund_commitments__amount',
      key: 'amount',
      align: 'right' as const,
      render: (v: number | null) => figure(v)
    },
    {
      title: 'Fees contributed',
      dataIndex: 'prog__actual_fees',
      key: 'fees',
      align: 'right' as const,
      render: (v: number | null) => figure(v)
    },
    {
      title: 'Fees share',
      dataIndex: 'prog__target_fees',
      key: 'target',
      align: 'right' as const,
      render: (v: number | null) => figure(v)
    },
    {
      title: 'Δ Fees',
      dataIndex: 'prog__delta_fees',
      key: 'dFees',
      align: 'right' as const,
      render: (v: number | null) => figure(v, true)
    },
    {
      title: 'Δ Investments',
      dataIndex: 'prog__delta_investment',
      key: 'dInv',
      align: 'right' as const,
      render: (v: number | null) => figure(v)
    },
    {
      title: 'Δ Expenses',
      dataIndex: 'prog__delta_expenses',
      key: 'dExp',
      align: 'right' as const,
      render: (v: number | null) => figure(v)
    },
    {
      title: 'Cash residual',
      dataIndex: 'prog__residual',
      key: 'residual',
      align: 'right' as const,
      render: (v: number | null) =>
        // beyond a dollar the cash is out of sync (the SQL holds the reclass
        // at the same threshold); cents are split rounding
        Math.abs(v ?? 0) > 1 ? <Tag color='gold'>{figure(v)}</Tag> : figure(v)
    }
  ]

  const eventColumns = [
    {
      title: 'As at',
      dataIndex: 'fund_group_fee_reallocations__as_of',
      key: 'asOf',
      width: 110,
      render: (v: string) => fmtDate(v)
    },
    ...(
      [
        ['prog__satellite_fee_status', `${sat?.funds__name ?? 'Satellite'} true-up`],
        ['prog__primary_fee_status', `${sat?.prog__primary_fund_name ?? 'Primary'} true-up`],
        ['prog__satellite_journal_status', 'Reclass']
      ] as const
    ).map(([k, title]) => ({
      title,
      dataIndex: k,
      key: k,
      render: (v: string | null) => {
        if (!v) return <Text type='secondary'>—</Text>
        const t = statusTag(v)
        return <Tag color={t.color}>{t.label}</Tag>
      }
    })),
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, e: FundGroupFeeReallocationEvent) => {
        const booked = [
          e.prog__satellite_fee_status,
          e.prog__primary_fee_status,
          e.prog__satellite_journal_status,
          e.prog__primary_journal_status
        ].includes('Booked')
        return booked ? null : (
          <Button size='small' danger disabled={acting} onClick={() => handleDelete(e)}>
            Delete
          </Button>
        )
      }
    }
  ]

  // nothing on this group reallocates: no section at all
  if (!loading && data && satellites.length === 0) return null

  const due = steps.some((s) => s.status === 'Due')

  return (
    <div ref={activateRef}>
      <FormSection
        title='Late-joiner Fee Reallocation'
        titleColor='purple'
        loading={loading}
        headerActions={
          <Space>
            {satellites.length > 1 ? (
              <Select
                size='small'
                style={{ minWidth: 180 }}
                value={sat?.funds__id}
                onChange={(v: string) => setFundId(v)}
                options={satellites.map((s) => ({ value: s.funds__id, label: s.funds__name ?? s.funds__id }))}
              />
            ) : null}
            <Select
              size='small'
              style={{ minWidth: 170 }}
              value={sat?.prog__as_of}
              onChange={(v: string) => setAsOf(v)}
              options={(sat?.prog__admission_dates ?? []).map((d) => ({ value: d, label: `As at ${fmtDate(d)}` }))}
              placeholder='Admission date'
            />
            <Button type='primary' size='small' onClick={handleDraft} disabled={!sat || acting || !due}>
              Draft Reallocation
            </Button>
            <Button icon={<ReloadOutlined />} size='small' onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        {modalContextHolder}
        {loading && !data ? (
          <div className='flex justify-center p-6'>
            <Spin />
          </div>
        ) : sat ? (
          <div className='flex flex-col gap-3'>
            <Text type='secondary'>
              {sat.funds__name ?? 'The satellite'} charges no catch-up fee: its fees to date are shared by every
              partner admitted, as if all had been in from the start, and the{' '}
              {sat.prog__primary_fund_name ?? 'primary'} cross-credit follows. Transfers stay as made; this books the
              accounting as at the admission date ({fmtDate(sat.prog__as_of)}, billing period{' '}
              {fmtDate(sat.prog__period_start)} – {fmtDate(sat.prog__period_end)}).
            </Text>
            {sat.prog__satellite_fee_warning ? (
              <Alert type='warning' showIcon message={sat.prog__satellite_fee_warning} />
            ) : null}
            <Table
              size='small'
              pagination={false}
              rowKey='key'
              dataSource={steps}
              columns={stepColumns}
              scroll={{ x: 'max-content' }}
            />
            <div>
              <Text strong className='block mb-1'>
                Fee true-ups — due to date minus booked, per investor
              </Text>
              <Table
                size='small'
                pagination={false}
                rowKey='key'
                dataSource={feeRows}
                columns={feeColumns}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No admitted investors' }}
              />
            </div>
            <div>
              <Text strong className='block mb-1'>
                Contribution reclass — {sat.funds__name ?? 'satellite'} (mirrored on {sat.prog__primary_fund_name ?? 'the primary'})
              </Text>
              <Table
                size='small'
                pagination={false}
                rowKey={(r: FundGroupFeeReclassRow) => r.fund_commitments__id}
                dataSource={sat.prog__reclass_rows}
                columns={reclassColumns}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No admitted investors' }}
              />
            </div>
            {sat.prog__events.length > 0 ? (
              <div>
                <Text strong className='block mb-1'>
                  Reallocations
                </Text>
                <Table
                  size='small'
                  pagination={false}
                  rowKey={(e: FundGroupFeeReallocationEvent) => e.fund_group_fee_reallocations__id}
                  dataSource={sat.prog__events}
                  columns={eventColumns}
                  scroll={{ x: 'max-content' }}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </FormSection>
    </div>
  )
}

export default FundGroupFeeReallocationSection
